import React, { useState } from 'react'
import { navigate, Redirect } from '@reach/router'
import { useWindowScroll, useClickAway, useMedia } from 'react-use'

// Config
import l, { isDgmt, isLevelUp, isSef, isSefEmployee } from 'config/localization'
import constants from 'config/constants'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import CookieHeader from 'components/CookieHeader'
import FlashMessage from 'components/FlashMessage'
import HelpModal from 'components/HelpModal'
import ProfileMenu from 'components/ProfileMenu'
import Tooltip from 'components/Tooltip'

// Styles
import {
  ChampionBtn,
  DgmtNewPboButton,
  FeedbackIconLink,
  FeedbackIconWrapper,
  LevelUpLogoLink,
  LevelUpNavBarContentItem,
  LevelUpNavContainer,
  LevelUpNavIcon,
  LevelUpNavLinkItem,
  LevelUpNavLinkWrapper,
  LogoLink,
  MainNavWrapper,
  NavBar,
  NavBarContent,
  NavBarContentItem,
  NavBarRightSection,
  NavLinkItem,
  NavLinkItemWrapper,
  NavLinksWrapper,
  PricingDropdownItem,
  PricingDropdownWrapper,
  ScrollToTop,
  SefNavButton,
  SefNavButtonContents,
  SefNavButtonHint,
  SefNavButtonWrapper,
  SvgIconWrapper,
  TitleText,
  TranslateIconContainer
} from './styles'

// icons
import { PatchQuestionFill } from '@styled-icons/bootstrap/PatchQuestionFill'
import { StarFill } from '@styled-icons/bootstrap'
import { Translate } from '@styled-icons/bootstrap/Translate'

// assets
import pngLogo from 'assets/images/bp-logo.png'
import svgLogo from 'assets/images/svgs/bp_logo.svg'
import dgmtLogo from 'assets/images/dgmt_logo_blue.jpg'
import lvlupHealIconWhite from 'assets/images/levelup/noun-heart-8069368-FFFFFF.png'
import lvlupLearnIconWhite from 'assets/images/levelup/noun-book-5899140-FFFFFF.png'
import lvlupDiscoverIconWhite from 'assets/images/levelup/noun-find-8104754-FFFFFF.png'
import lvlupShareIconWhite from 'assets/images/levelup/noun-speech-3647859-FFFFFF.png'
import lvlUpLogo from 'assets/images/levelup/lvlup_logo.png'

const SupportIcon = ({ helpModalState, menuModalIsOpen, setHelpModalState }) => {
  if (menuModalIsOpen) return null

  const backTo = window.location.pathname + window.location.search

  return (
    <FeedbackIconWrapper>
      <Tooltip
        position="left"
        content="Open help page"
        displayBlock
        forceShow
        translateY={10}
        useAutoWidth
      >
        <FeedbackIconLink onClick={() => navigate(`/help?backTo=${backTo}`)}>
          <PatchQuestionFill color="white" />
        </FeedbackIconLink>
      </Tooltip>

      <HelpModal setHelpModalState={setHelpModalState} showModal={helpModalState} />
    </FeedbackIconWrapper>
  )
}

const DiscoverDropdown = ({ goTo, discoverDropdownVisible, toggleDiscoverDropdown }) => {
  const ref = React.useRef(null)
  useClickAway(
    ref,
    () => {
      if (discoverDropdownVisible) {
        toggleDiscoverDropdown()
      }
    },
    ['click']
  )

  if (!discoverDropdownVisible) return null

  return (
    <PricingDropdownWrapper ref={ref}>
      <PricingDropdownItem onClick={() => goTo(`/search/challenges`)}>
        Search challenges
      </PricingDropdownItem>

      <PricingDropdownItem onClick={() => goTo(`/search/${l('ROUTE_OPPS')}`)}>
        Search {l('OPPS')}
      </PricingDropdownItem>

      <PricingDropdownItem onClick={() => goTo(`/search/nonprofits`)}>
        Search nonprofits
      </PricingDropdownItem>

      <PricingDropdownItem onClick={() => goTo(`/search/companies`)}>
        Search companies
      </PricingDropdownItem>

      <PricingDropdownItem onClick={() => goTo(`/${l('ROUTE_CAMPS')}`)}>
        Explore {l('CAMPS')}
      </PricingDropdownItem>

      <PricingDropdownItem onClick={() => goTo(`/categories`)}>
        Explore categories
      </PricingDropdownItem>
    </PricingDropdownWrapper>
  )
}

const PricingDropdown = ({ goTo, pricingDropdownVisible, togglePricingDropdown }) => {
  const ref = React.useRef(null)
  useClickAway(
    ref,
    () => {
      if (pricingDropdownVisible) {
        togglePricingDropdown()
      }
    },
    ['click']
  )

  if (!pricingDropdownVisible) return null

  return (
    <PricingDropdownWrapper ref={ref}>
      <PricingDropdownItem onClick={() => goTo(`/pricing/nonprofits`)}>
        Nonprofits
      </PricingDropdownItem>
      <PricingDropdownItem onClick={() => goTo(`/pricing/business`)}>Business</PricingDropdownItem>
      <PricingDropdownItem onClick={() => goTo(`/pricing/education`)}>
        Education
      </PricingDropdownItem>
    </PricingDropdownWrapper>
  )
}

const determineButtonVisibilities = ({ hideSefCta, isDashboardPage, isLoggedIn }) => {
  if (isDashboardPage) {
    return { showAccessDashboardButton: false, showProfileMenu: true }
  }

  if (!isLoggedIn) {
    return { showAccessDashboardButton: !hideSefCta, showProfileMenu: false }
  }

  return { showAccessDashboardButton: !hideSefCta, showProfileMenu: !!hideSefCta }
}

const CenterCta = ({ goTo, isLoading, isLoggedIn, roleData }) => {
  if (isLoading) return null

  const hasGodAdminRole = roleData.some(r => r.role_type === constants.ROLES.GOD_MODE)
  const hasIcasaRole = roleData.some(r => r.role_type === constants.ROLES.DGMT_ICASA_ADMIN)
  const hasMnoRole = roleData.some(role => role.role_type === constants.ROLES.DGMT_MNO_ADMIN)
  const hasDgmtAdminRole = roleData.some(r => r.role_type === constants.ROLES.DGMT_ADMIN)

  const isPrivilegedRole = hasGodAdminRole || hasDgmtAdminRole || hasIcasaRole || hasMnoRole
  if (isPrivilegedRole) return null

  const buttonLabel = isLoggedIn ? 'Submit website' : 'Apply as PBO'
  const buttonPath = isLoggedIn ? '/new-website' : `/new-pbo`

  return (
    <NavBarContentItem justifyContent="center">
      <NavLinksWrapper>
        <div onClick={() => goTo(buttonPath)}>
          <NavLinkItem>
            <DgmtNewPboButton>{buttonLabel}</DgmtNewPboButton>
          </NavLinkItem>
        </div>
      </NavLinksWrapper>
    </NavBarContentItem>
  )
}

const NavBarComponent = ({
  hideSefCta,
  isDashboardPage,
  isLoading,
  isLoggedIn,
  discoverDropdownVisible,
  menuModalIsOpen,
  notifications,
  pageType,
  pricingDropdownVisible,
  roleData = [],
  toggleInternationalizationMenu,
  toggleDiscoverDropdown,
  toggleMenuModal,
  togglePricingDropdown,
  userData
}) => {
  const isHomepage = ['homePage', 'dgmtHomePage'].includes(pageType)

  // const isTermsPage = pageType === 'termsPage'
  // const isStoryPage = pageType === 'storyPage'
  // const isPrivacyPage = pageType === 'privacyPage'

  if (userData && userData.on_probation) {
    return <Redirect to={`/probation`} noThrow />
  }

  const goTo = link => {
    toggleMenuModal('close')
    navigate(link)
  }

  const { y } = useWindowScroll()
  const transparent = y < 85

  // disabled until we get the translations
  const translationsEnabled = false

  const isWide = useMedia('(min-width: 769px)')
  const logo = isWide ? svgLogo : pngLogo
  const logoHeight = isWide ? '44px' : 'auto'
  const logoWidth = isWide ? '121px' : '25px'

  const [helpModalState, setHelpModalState] = useState(false)

  if (isLevelUp) {
    return (
      <MainNavWrapper homepage={isHomepage}>
        <NavBar background="#1d3f57" isLoading={isLoading} homepage={isHomepage}>
          <NavBarContent>
            <NavBarContentItem>
              <LevelUpLogoLink onClick={() => goTo('/')}>
                <img src={lvlUpLogo} alt="LevelUp logo" />
              </LevelUpLogoLink>
            </NavBarContentItem>

            <LevelUpNavContainer>
              <LevelUpNavBarContentItem>
                <LevelUpNavLinkWrapper onClick={() => navigate('/learn')}>
                  <LevelUpNavIcon src={lvlupLearnIconWhite} />
                  <LevelUpNavLinkItem>Learn</LevelUpNavLinkItem>
                </LevelUpNavLinkWrapper>
              </LevelUpNavBarContentItem>

              <LevelUpNavBarContentItem>
                <LevelUpNavLinkWrapper onClick={() => navigate('/heal')}>
                  <LevelUpNavIcon src={lvlupHealIconWhite} />
                  <LevelUpNavLinkItem>Heal</LevelUpNavLinkItem>
                </LevelUpNavLinkWrapper>
              </LevelUpNavBarContentItem>

              <LevelUpNavBarContentItem>
                <LevelUpNavLinkWrapper onClick={() => navigate('/discover')}>
                  <LevelUpNavIcon src={lvlupDiscoverIconWhite} />
                  <LevelUpNavLinkItem>Discover</LevelUpNavLinkItem>
                </LevelUpNavLinkWrapper>
              </LevelUpNavBarContentItem>

              <LevelUpNavBarContentItem>
                <LevelUpNavLinkWrapper onClick={() => navigate('/share')}>
                  <LevelUpNavIcon src={lvlupShareIconWhite} />
                  <LevelUpNavLinkItem>Share</LevelUpNavLinkItem>
                </LevelUpNavLinkWrapper>
              </LevelUpNavBarContentItem>
            </LevelUpNavContainer>

            <NavBarContentItem justifyContent="flex-end">
              <div onClick={() => navigate('/start')}>
                <NavLinkItem>
                  <DgmtNewPboButton>Start</DgmtNewPboButton>
                </NavLinkItem>
              </div>
            </NavBarContentItem>
          </NavBarContent>
        </NavBar>

        <CookieHeader />
        <FlashMessage />
      </MainNavWrapper>
    )
  }

  if (isDgmt) {
    return (
      <MainNavWrapper homepage={isHomepage} transparent={transparent}>
        <NavBar isLoading={isLoading} homepage={isHomepage} transparent={transparent}>
          <NavBarContent>
            <NavBarContentItem>
              <LogoLink onClick={() => goTo('/')}>
                <img src={dgmtLogo} width="44" height="44" alt="DGMT logo" />
              </LogoLink>
            </NavBarContentItem>

            <CenterCta
              goTo={goTo}
              isLoading={isLoading}
              isLoggedIn={isLoggedIn}
              roleData={roleData}
            />

            <NavBarContentItem justifyContent="flex-end">
              <SupportIcon
                helpModalState={helpModalState}
                menuModalIsOpen={menuModalIsOpen}
                setHelpModalState={setHelpModalState}
              />

              <NavBarRightSection>
                <ConditionalDisplay displayWhen={[translationsEnabled]}>
                  <TranslateIconContainer onClick={toggleInternationalizationMenu}>
                    <Translate />
                  </TranslateIconContainer>
                </ConditionalDisplay>

                <ProfileMenu
                  menuModalIsOpen={menuModalIsOpen}
                  notifications={notifications}
                  toggleMenuModal={toggleMenuModal}
                />
              </NavBarRightSection>
            </NavBarContentItem>
          </NavBarContent>
        </NavBar>

        <CookieHeader />
        <FlashMessage />
      </MainNavWrapper>
    )
  }

  if (isSef || isSefEmployee) {
    const isHidden = y > 85 && isHomepage

    const { showAccessDashboardButton, showProfileMenu } = determineButtonVisibilities({
      hideSefCta,
      isDashboardPage,
      isLoggedIn
    })

    return (
      <MainNavWrapper hidden={isHidden} homepage={isHomepage} transparent={transparent}>
        <NavBar isLoading={isLoading} homepage={isHomepage} transparent={transparent}>
          <NavBarContent>
            <LogoLink homepage={isHomepage} isSef onClick={() => goTo('/')}>
              <img src={logo} width={logoWidth} height={logoHeight} alt="Brownie Points logo" />
            </LogoLink>

            <NavBarRightSection>
              <ScrollToTop />

              <SupportIcon
                helpModalState={helpModalState}
                menuModalIsOpen={menuModalIsOpen}
                setHelpModalState={setHelpModalState}
              />

              {showProfileMenu && (
                <ProfileMenu
                  menuModalIsOpen={menuModalIsOpen}
                  notifications={notifications}
                  toggleMenuModal={toggleMenuModal}
                />
              )}

              {showAccessDashboardButton && (
                <SefNavButtonWrapper>
                  <SefNavButtonHint>Already using our platform?</SefNavButtonHint>

                  <SefNavButton onClick={() => navigate(`/dashboard`)}>
                    <SefNavButtonContents>
                      <SvgIconWrapper>
                        <StarFill />
                      </SvgIconWrapper>

                      <TitleText>Access dashboard</TitleText>
                    </SefNavButtonContents>
                  </SefNavButton>
                </SefNavButtonWrapper>
              )}
            </NavBarRightSection>
          </NavBarContent>
        </NavBar>

        <CookieHeader />
        <FlashMessage />
      </MainNavWrapper>
    )
  }

  return (
    <MainNavWrapper homepage={isHomepage} transparent={transparent}>
      <NavBar isLoading={isLoading} homepage={isHomepage} transparent={transparent}>
        <NavBarContent>
          <LogoLink onClick={() => goTo(isLoggedIn ? '/feed' : '/')}>
            <img src={logo} width={logoWidth} height={logoHeight} alt="Brownie Points logo" />
          </LogoLink>

          <NavLinksWrapper>
            <div
              onClick={() => {
                if (isLoggedIn) {
                  goTo(`/dashboard/stories/new?eventType=GoodDeed&chooseTemplate`)
                } else {
                  goTo(`/feed`)
                }
              }}
            >
              <NavLinkItem>
                <ChampionBtn>{isLoggedIn ? 'Share story' : 'Stories'}</ChampionBtn>
              </NavLinkItem>
            </div>

            <NavLinkItemWrapper onClick={() => toggleDiscoverDropdown()}>
              <NavLinkItem>Explore</NavLinkItem>

              <DiscoverDropdown
                goTo={goTo}
                discoverDropdownVisible={discoverDropdownVisible}
                toggleDiscoverDropdown={toggleDiscoverDropdown}
              />
            </NavLinkItemWrapper>

            <NavLinkItemWrapper onClick={() => togglePricingDropdown()}>
              <NavLinkItem>Solutions</NavLinkItem>

              <PricingDropdown
                goTo={goTo}
                pricingDropdownVisible={pricingDropdownVisible}
                togglePricingDropdown={togglePricingDropdown}
              />
            </NavLinkItemWrapper>
          </NavLinksWrapper>

          <NavBarRightSection>
            <ScrollToTop />

            <ProfileMenu
              menuModalIsOpen={menuModalIsOpen}
              notifications={notifications}
              toggleMenuModal={toggleMenuModal}
              userData={userData}
            />
          </NavBarRightSection>
        </NavBarContent>
      </NavBar>

      <CookieHeader />
      <FlashMessage />
    </MainNavWrapper>
  )
}

export default NavBarComponent
